import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import classNames from 'classnames'
import { searchLocationsBySerial } from './libs/fetchData'
import translate from 'libs/translate'
import { icons } from './libs/assets'
import { Overlay } from 'components/Overlay'
import CheckList from 'components/CheckList/component'
import Show from 'components/Show/component'
import { defaultMarkerFilter, connectorNames } from './libs/getDefaultOptions'
import _ from 'lodash'

import './ToolBox.scss'

const ToolBox = React.forwardRef(({ onGoToMyPositionClick, currentFilter, toggleFilterModal, ...rest }, ref) => (
    <div ref={ref} className='ToolBox'>
        <div className='ToolBox__actions'>
            <GoToMyPosition onClick={onGoToMyPositionClick} />
            <OpenFilter
                currentFilter={currentFilter}
                toggleFilterModal={toggleFilterModal} />
        </div>
        <div className='ToolBox__search-container'>
            <Search {...rest} />
        </div>
    </div>
))
ToolBox.propTypes = {
    onGoToMyPositionClick: PropTypes.func,
    currentFilter: PropTypes.object,
    toggleFilterModal: PropTypes.func
}

const Search = ({ onMarkerSelected, google, map, onPlaceSelected }) => {
    const [searchBoxCreated, setSearchBoxCreated] = useState(false)
    const [mode, setMode] = useState('location')
    const searchRef = useRef(null);

    useEffect(() => {
        if (!searchBoxCreated && google) {
            const searchBox = new google.maps.places.Autocomplete(searchRef.current)
            searchBox.bindTo('bounds', map)

            searchBox.addListener('place_changed', () => {
                const place = searchBox.getPlace()

                if (place) {
                    onPlaceSelected({
                        address: place.formatted_address,
                        coordinates: {
                            lat: place.geometry.location.lat(),
                            lng: place.geometry.location.lng()
                        }
                    })
                }
            })

            setSearchBoxCreated(true)
        }
    }, [google, map, onPlaceSelected, searchBoxCreated])

    const c1 = classNames({ hidden: mode !== 'location' })
    const c2 = classNames({ hidden: mode !== 'serial' })

    const icon = {
        'serial': <img src={icons.searchByLocationExternalId.default} />,
        'location': <img src={icons.searchByLocation.default} />
    }[mode]

    return (
        <div className='ToolBox__search'>
            <div className='input-group'>
                <div className='input-group-btn'>
                    <button type='button' className='btn btn-default dropdown-toggle' data-toggle='dropdown' aria-haspopup='true' aria-expanded='false'>
                        <span className='ToolBox__search__icon'>{icon}</span> {' '}<span className='caret' />
                    </button>
                    <ul className='dropdown-menu'>
                        <li><a href='#' onClick={() => setMode('location')}>
                            <span className='ToolBox__search__icon'>
                                <img src={icons.searchByLocation.default} />
                            </span>{translate('landing_search_by_location')}
                        </a>
                        </li>
                        <li><a href='#' onClick={() => setMode('serial')}>
                            <span className='ToolBox__search__icon'>
                                <img src={icons.searchByLocationExternalId.default} />
                            </span>{translate('landing_search_by_serial')}
                        </a>
                        </li>
                    </ul>
                </div>
                <div className={c1}>
                    <input
                        ref={searchRef}
                        placeholder={translate('landing_search_by_location')}
                        className='ToolBox__search__input form-control' />
                </div>
                <div className={c2}>
                    <SearchBySerial onMarkerSelected={onMarkerSelected} />
                </div>
            </div>
        </div>
    )
}
Search.propTypes = {
    onMarkerSelected: PropTypes.func,
    google: PropTypes.object,
    map: PropTypes.object,
    onPlaceSelected: PropTypes.func
}

const OpenFilter = ({ currentFilter, toggleFilterModal }) => {
    const isDefaultFilter = _.isEqual(currentFilter, defaultMarkerFilter)

    return (
        <div className='ToolBox__OpenFilter'>
            <Overlay
                preventOverflow={{ marginLeft: '-25px', left: 0 }}
                overlay={
                    <FilterSummary
                        toggleFilterModal={toggleFilterModal}
                        filter={currentFilter}
                    />}>
                <button className='btn btn-default' onClick={() => toggleFilterModal(true)}>
                    {isDefaultFilter
                        ? <img width='16px' src={icons.filterOff.default} />
                        : <img width='20px' src={icons.filterOn.default} />
                    }
                </button>
            </Overlay>
        </div>
    )
}

OpenFilter.propTypes = {
    currentFilter: PropTypes.object,
    toggleFilterModal: PropTypes.func,
}

const FilterSummary = ({ filter }) => {
    return (
        <div className='FilterSummary'>
            <CheckList>
                <Show if={filter.showOnlyAvailable}>
                    <li>{translate('landing_filter_show_only_available')}</li>
                </Show>
                <Show if={filter.onlyAccessibleWithCard}>
                    <li>{translate('landing_filter_only_tnm_card')}</li>
                </Show>
            </CheckList>

            <h3>{translate('landing_filter_title_power')}</h3>
            <div className='FilterSummary__max-power'>&gt; {filter.minPower}
                <span className='FilterSummary__max-power__unit'>(Kw)</span></div>
            <h3>{translate('landing_filter_title_connector_type')}</h3>
            <CheckList>{
                filter.connectors
                    .map(c => typeof connectorNames[c] === 'function' ? connectorNames[c]() : connectorNames[c])
                    .sort()
                    .map((c) => <li key={c}>{c}</li>)}
            </CheckList>
        </div>
    )
}
FilterSummary.propTypes = {
    filter: PropTypes.object,
}

const GoToMyPosition = ({ onClick }) => {
    const [loading, setLoading] = useState(false)
    const cl = classNames('ToolBox__GoToMyPosition', { loading })

    return (
        <div className={cl}>
            <button className='btn btn-default'
                onClick={() => {
                    setLoading(true)
                    onClick(() => setLoading(false))
                }}>
                <img src={icons.myPosition.default} width='20px' />
            </button>
        </div>
    )
}
GoToMyPosition.propTypes = {
    onClick: PropTypes.func
}

const SearchBySerial = ({ onMarkerSelected }) => {
    const [results, setResults] = useState([])
    const resultsCl = classNames('SearchBySerial__results',
        { hidden: results.length === 0 })

    const search = async (serial) => setResults(await searchLocationsBySerial(serial))

    const onClick = (data) => {
        onMarkerSelected(data)
        setResults([])
    }

    return (<div className='SearchBySerial'>
        <input
            onChange={({ target }) => { if (target.value) search(target.value) }}
            onFocus={({ target }) => { if (target.value) search(target.value) }}
            placeholder={translate('landing_search_by_serial')}
            className='form-control'
        />
        <div className={resultsCl}>
            {results.map((value) =>
                <div
                    key={value.uid}
                    className='SearchBySerial__results__item'
                    onClick={() => onClick(value)}>
                    <span className='SearchBySerial__results__item__serial'>
                        {value.externalId}
                    </span>
                    <span className='SearchBySerial__results__item__operator'>
                        {value.operatorName}
                    </span>
                </div>
            )}
        </div>
    </div>)
}
SearchBySerial.propTypes = {
    onMarkerSelected: PropTypes.func
}

export default ToolBox
